import React from 'react';
import InsightArticle from '../../components/InsightArticle';
import { useLanguage } from '../../context/LanguageContext';

const SpeedToLeadMath = () => {
  const { lang } = useLanguage();

  const content = {
    en: {
      title: "The 'Speed-to-Lead' Math: Why 5 Minutes is the Kill Zone",
      description: "Why response time, not lead count, is the single most important metric for local business revenue.",
      category: "Customer Acquisition",
      date: "Jan 22, 2026",
      keyTakeaway: "Past 5 minutes, you aren't continuing a conversation—you're interrupting a different one. Response time is the physics of conversion.",
      body: (
        <>
          <p className="mb-6 text-gray-300 leading-relaxed text-lg">
            Every local business tracks the wrong number. They track how many leads came in this month. Almost nobody tracks how long it took to respond to each one — and that number, <span className="text-orange-500 font-semibold">not the lead count</span>, is what actually decides revenue.
          </p>

          <h3 className="text-2xl font-bold text-white mt-10 mb-4">The math, stripped down</h3>
          <p className="mb-6 text-gray-300 leading-relaxed text-lg">
            Response-time research across industries (sales, real estate, local services) consistently shows the same pattern: contact a lead <span className="text-orange-500 font-semibold">within 5 minutes</span>, and you're dramatically more likely to reach and convert them than if you wait even 30 minutes. Wait an hour, and the odds drop again.
          </p>
          <p className="mb-6 text-gray-300 leading-relaxed text-lg">
            Wait until "later today," and you're often competing with someone who already replied — because the person messaged more than one business, and interest fades the moment the initial impulse passes.
          </p>
          <p className="mb-6 text-gray-300 leading-relaxed text-lg">
            This isn't about laziness. It's <span className="text-orange-500 font-semibold">physics</span>. Interest is a decaying signal. Someone messaging a gym or a clinic at 9pm on a Tuesday is in a specific mental state — curious, maybe a little impulsive, weighing a decision. That state doesn't last. By the next morning, they've either moved on, messaged a competitor, or just lost the spark that made them reach out in the first place.
          </p>

          <h3 className="text-2xl font-bold text-white mt-10 mb-4">Why 5 minutes specifically</h3>
          <p className="mb-6 text-gray-300 leading-relaxed text-lg">
            Five minutes is roughly the window where a reply still <span className="text-orange-500 font-semibold">feels like a conversation</span> instead of a follow-up. Inside that window, the person is often still on their phone, still in the mindset that made them message you.
          </p>
          <p className="mb-6 text-gray-300 leading-relaxed text-lg italic">
            Outside it, you're not continuing their thought anymore — you're <span className="text-orange-500 font-semibold">interrupting</span> whatever they've moved on to. This is the "kill zone": the point past which the lead isn't dead, but the odds have quietly collapsed.
          </p>

          <h3 className="text-2xl font-bold text-white mt-10 mb-4">Why almost no local business hits this</h3>
          <p className="mb-6 text-gray-300 leading-relaxed text-lg">
            Front desk staff are busy with people physically in the room. Clinic owners are mid-treatment. Gym trainers are mid-class. The DM sits there, technically unanswered but not forgotten — just delayed by the reality of running a business with actual humans in it who can't watch a phone all day.
          </p>
          <p className="mb-6 text-gray-300 leading-relaxed text-lg">
            This isn't a discipline problem. It's a <span className="text-orange-500 font-semibold">structural one</span>. Expecting a person to hit a 5-minute window consistently while doing their actual job is expecting something that will fail eventually.
          </p>

          <h3 className="text-2xl font-bold text-white mt-10 mb-4">What actually closes the gap</h3>
          <p className="mb-6 text-gray-300 leading-relaxed text-lg">
            The fix isn't "answer faster, try harder." It's removing the dependency on a human being available at the exact right second:
          </p>
          <ul className="list-disc pl-6 mb-8 text-gray-300 space-y-4 text-lg">
            <li>An <span className="text-white font-bold">instant automated first response</span> that answers the basic questions (price, hours, availability) the moment the message lands.</li>
            <li>A <span className="text-white font-bold">direct booking option</span> inside that first reply, so the lead can act immediately instead of waiting for a human to catch up.</li>
            <li>A <span className="text-white font-bold">clear handoff point</span> where a real person takes over for anything beyond the basics.</li>
          </ul>

          <h3 className="text-2xl font-bold text-white mt-10 mb-4">The number worth actually tracking</h3>
          <p className="mb-6 text-gray-300 leading-relaxed text-lg">
            Forget "how many DMs did we get this month." Track: <span className="text-orange-500 font-semibold">what's our average first-response time</span>, and how does our booking rate change above and below the 5-minute mark.
          </p>
          <div className="mt-12 p-8 bg-zinc-900 border border-orange-500/30 rounded-[2rem]">
            <p className="text-xl text-white font-bold mb-4">
              This is the exact gap my AI Lead Assistant closes.
            </p>
            <p className="text-gray-400 mb-6">
              It answers Instagram DMs the instant they land — no 5-minute window missed, no lead going cold overnight.
            </p>
            <p className="text-orange-500 font-black uppercase tracking-widest text-sm">
              Test it yourself: send "SYSTEM" to my Instagram.
            </p>
          </div>
        </>
      )
    },
    es: {
      title: "Matemáticas del 'Speed-to-Lead': Por qué 5 Minutos es la Zona de Muerte",
      description: "Por qué el tiempo de respuesta, y no el número de leads, es la métrica más importante para los ingresos de un negocio local.",
      category: "Adquisición de Clientes",
      date: "22 Ene 2026",
      keyTakeaway: "Pasados los 5 minutos, no estás continuando una conversación, estás interrumpiendo otra distinta. El tiempo de respuesta es la física de la conversión.",
      body: (
        <>
          <p className="mb-6 text-gray-300 leading-relaxed text-lg">
            Todos los negocios locales rastrean el número equivocado. Rastrean cuántos leads entraron este mes. Casi nadie rastrea cuánto tiempo se tardó en responder a cada uno — y ese número, <span className="text-orange-500 font-semibold">no el conteo de leads</span>, es lo que realmente decide los ingresos.
          </p>

          <h3 className="text-2xl font-bold text-white mt-10 mb-4">Las matemáticas, sin adornos</h3>
          <p className="mb-6 text-gray-300 leading-relaxed text-lg">
            La investigación sobre el tiempo de respuesta en diversas industrias (ventas, inmobiliaria, servicios locales) muestra siempre el mismo patrón: contacta a un lead <span className="text-orange-500 font-semibold">dentro de los primeros 5 minutos</span>, y tendrás drásticamente más probabilidades de convertirlo.
          </p>
          <p className="mb-6 text-gray-300 leading-relaxed text-lg">
            Si esperas hasta "más tarde", a menudo estarás compitiendo con alguien que ya respondió, porque el interés se desvanece en el momento en que pasa el impulso inicial.
          </p>
          <p className="mb-6 text-gray-300 leading-relaxed text-lg">
            Esto no se trata de pereza. Es <span className="text-orange-500 font-semibold">física</span>. El interés es una señal que se degrada. Alguien que escribe a un gimnasio a las 9 de la noche de un martes está en un estado mental específico. Ese estado no dura.
          </p>

          <h3 className="text-2xl font-bold text-white mt-10 mb-4">Por qué 5 minutos específicamente</h3>
          <p className="mb-6 text-gray-300 leading-relaxed text-lg">
            Cinco minutos es la ventana donde una respuesta todavía <span className="text-orange-500 font-semibold">se siente como una conversación</span> en lugar de un seguimiento. Dentro de esa ventana, la persona suele estar todavía con el móvil en la mano.
          </p>
          <p className="mb-6 text-gray-300 leading-relaxed text-lg italic">
            Fuera de ella, ya no estás continuando su pensamiento; estás <span className="text-orange-500 font-semibold">interrumpiendo</span> lo que sea que estén haciendo ahora. Esta es la "zona de muerte".
          </p>

          <h3 className="text-2xl font-bold text-white mt-10 mb-4">Por qué casi ningún negocio local lo logra</h3>
          <p className="mb-6 text-gray-300 leading-relaxed text-lg">
            El personal de recepción está ocupado. Los dueños de clínicas están tratando pacientes. Los entrenadores están dando clase. No es un problema de disciplina, es un <span className="text-orange-500 font-semibold">problema estructural</span>.
          </p>

          <h3 className="text-2xl font-bold text-white mt-10 mb-4">Lo que realmente cierra la brecha</h3>
          <p className="mb-6 text-gray-300 leading-relaxed text-lg">
            La solución no es "responde más rápido, esfuérzate más". Es eliminar la dependencia de que un humano esté disponible en el segundo exacto:
          </p>
          <ul className="list-disc pl-6 mb-8 text-gray-300 space-y-4 text-lg">
            <li>Una <span className="text-white font-bold">primera respuesta automática instantánea</span> que resuelva dudas básicas.</li>
            <li>Una <span className="text-white font-bold">opción de reserva directa</span> dentro de esa respuesta.</li>
            <li>Un <span className="text-white font-bold">punto de entrega claro</span> donde una persona real toma el control para lo complejo.</li>
          </ul>

          <h3 className="text-2xl font-bold text-white mt-10 mb-4">La métrica que realmente vale la pena medir</h3>
          <p className="mb-6 text-gray-300 leading-relaxed text-lg">
            Olvida cuántos DMs recibiste. Rastrera: <span className="text-orange-500 font-semibold">¿cuál es nuestro tiempo medio de respuesta?</span>.
          </p>
          <div className="mt-12 p-8 bg-zinc-900 border border-orange-500/30 rounded-[2rem]">
            <p className="text-xl text-white font-bold mb-4">
              Esta es exactamente la brecha que cierra mi Asistente de IA.
            </p>
            <p className="text-gray-400 mb-6">
              Responde DMs de Instagram al instante, sin perder nunca la ventana de 5 minutos.
            </p>
            <p className="text-orange-500 font-black uppercase tracking-widest text-sm">
              Pruébalo tú mismo: envía "SISTEMA" a mi Instagram.
            </p>
          </div>
        </>
      )
    }
  };

  const t = content[lang] || content.en;

  return (
    <InsightArticle
      title={t.title}
      description={t.description}
      category={t.category}
      date={t.date}
      readTime="5"
      keyTakeaway={t.keyTakeaway}
    >
      {t.body}
    </InsightArticle>
  );
};

export default SpeedToLeadMath;